import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ListingCard from "@/components/ListingCard";
import { Crosshair } from "@/components/Crosshair";
import { BadgeRow, SpecialtyRow } from "@/components/Badges";
import FoundingClaimPrompt from "@/components/founding/FoundingClaimPrompt";
import { VerificationEdge } from "@/components/verify/VerificationEdge";
import { FoundingSeatChip } from "@/components/FoundingSeatChip";
import FoundingShareCard from "@/components/founding/FoundingShareCard";
import { VouchMark } from "@/components/VouchMark";
import { BlockUserButton } from "@/components/BlockUserButton";
import { Star, Calendar, Clock, Flag, X, Repeat, Camera, Pencil, Plus, Crop, Eye } from "lucide-react";
import { toast } from "@/lib/toast";
import { ImageCropper } from "@/components/ImageCropper";
import MemberWarningsBanner from "@/components/MemberWarningsBanner";

export default function Profile() {
  const params = useParams();
  const { user } = useAuth();
  // `/profile` (no :username param) = the logged-in member's own profile; `/u/:username` = anyone.
  const username = params.username || user?.username;
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropper, setCropper] = useState(null); // { source:{dataUrl,width,height}, initialTransform }
  const [cropBusy, setCropBusy] = useState(false);
  const [galleryOverride, setGalleryOverride] = useState(null); // local source of truth after edits
  const [lightbox, setLightbox] = useState(null); // url of enlarged photo
  const [movedFrom, setMovedFrom] = useState(location.state?.movedFrom || null);
  const [listings, setListings] = useState([]);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCat, setReportCat] = useState("");
  const [reportCats, setReportCats] = useState([]);
  const [reportComment, setReportComment] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const load = () => {
    setNotFound(false);
    if (!username) return;
    api.get(`/users/${username}`).then(({ data }) => {
      setData(data);
      setBlocked(!!data.blocked_by_me);
      api.get(`/listings?seller_id=${data.user.user_id}`).then(({ data: d }) => setListings(d.listings)).catch(() => {});
    }).catch(() => {
      // A released handle stays reserved 90 days and redirects to the account's current
      // name so shared links never break; otherwise it's genuinely not found.
      api.get(`/username/resolve/${username}`).then(({ data: r }) => {
        if (r.status === "moved" && r.username && r.username !== username) {
          navigate(`/u/${r.username}`, { replace: true, state: { movedFrom: username } });
        } else {
          setNotFound(true);
        }
      }).catch(() => setNotFound(true));
    });
  };
  const onAvatarFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // Decode on the server (handles HEIC + EXIF orientation, strips GPS) into a browser-safe
    // source, then open the interactive cropper. No auto-trim — the user positions it.
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/me/image/decode", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCropper({ source: { dataUrl: data.data_url, width: data.width, height: data.height }, initialTransform: null });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't read that image.");
    } finally {
      setUploading(false);
    }
  };
  const openRecrop = async () => {
    setUploading(true);
    try {
      const { data } = await api.get("/me/avatar/source");
      if (!data.original_url) { toast.error("No saved original to re-crop — upload a new one."); return; }
      const dims = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = rej; im.src = data.original_url; });
      setCropper({ source: { dataUrl: data.original_url, width: dims.w, height: dims.h }, initialTransform: data.crop || null });
    } catch (err) {
      toast.error("Couldn't load the original to re-crop.");
    } finally {
      setUploading(false);
    }
  };
  const saveCrop = async (blob, transform) => {
    setCropBusy(true);
    try {
      const isBiz = (data?.user?.account_type === "business") || data?.user?.is_dealer;
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.webp", { type: "image/webp" }));
      const srcBlob = await (await fetch(cropper.source.dataUrl)).blob();
      fd.append("original", new File([srcBlob], "original.webp", { type: srcBlob.type || "image/webp" }));
      fd.append("crop", JSON.stringify(transform));
      const { data: r } = await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.profile_photos) setGalleryOverride(r.profile_photos);
      setData((d) => (d ? { ...d, user: { ...d.user, avatar: r.avatar, avatar_thumb: r.avatar_thumb } } : d));
      setCropper(null);
      toast.success(isBiz ? "Logo updated." : "Profile photo updated.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't save that image.");
    } finally {
      setCropBusy(false);
    }
  };
  const onAvatarRemove = async () => {
    setUploading(true);
    try {
      const { data: r } = await api.delete("/me/avatar");
      if (r.profile_photos !== undefined) setGalleryOverride(r.profile_photos);
      setData((d) => (d ? { ...d, user: { ...d.user, avatar: r.avatar || null, avatar_thumb: null } } : d));
      toast.success("Profile photo removed.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't remove that photo.");
    } finally {
      setUploading(false);
    }
  };
  const removePhoto = async (photoId) => {
    try {
      const { data: r } = await api.delete(`/me/photos/${photoId}`);
      setGalleryOverride(r.profile_photos);
      setData((d) => (d ? { ...d, user: { ...d.user, avatar: r.avatar || null } } : d));
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't remove that photo.");
    }
  };
  const setPrimaryPhoto = async (photoId) => {
    try {
      const { data: r } = await api.post(`/me/photos/${photoId}/primary`);
      setGalleryOverride(r.profile_photos);
      setData((d) => (d ? { ...d, user: { ...d.user, avatar: r.avatar } } : d));
      toast.success("Set as your profile photo.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't update.");
    }
  };
  useEffect(() => { setMovedFrom(location.state?.movedFrom || null); load(); }, [username]); // eslint-disable-line
  useEffect(() => {
    api.get("/moderation/categories").then(({ data }) => setReportCats((data.categories || []).filter((c) => c.reporter_selectable && (c.target === "user" || c.target === "both")))).catch(() => {});
  }, []);

  if (notFound) return (
    <div className="min-h-screen text-foreground" data-testid="profile-not-found">
      <Navbar />
      <div className="text-center py-24 px-4">
        <h1 className="font-heading text-3xl uppercase tracking-tight">Profile not found</h1>
        <p className="text-sm text-muted-foreground font-body mt-2">This account doesn't exist or is no longer available.</p>
        <button onClick={() => navigate("/feed")} className="mt-5 inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-4 py-2 transition-colors">Back to marketplace</button>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen text-foreground"><Navbar /><div className="text-center py-20 font-mono uppercase text-muted-foreground">Loading profile...</div></div>;

  const u = data.user;
  const isOwner = !!(user && user.user_id === u.user_id);
  // Preview mode: the OWNER can view their profile exactly as another member sees it. `data.user`
  // is ALREADY the strict public allowlist (public_profile_view) for every viewer, so this only
  // strips OWNER CHROME (edit controls, the owner-only vouch nudge) — it never hides or reveals a
  // field, so what the preview shows is byte-for-byte what a stranger's fetch returns.
  const previewPublic = new URLSearchParams(location.search).get("view") === "public" && isOwner;
  const isSelf = isOwner && !previewPublic;
  // Photo gallery (item 3). Self reads live from auth /me (has ids + effective cap); local
  // override reflects edits without a context refetch. Limit-1 accounts get NO gallery chrome.
  const selfLimit = user?.photo_limit || 1;
  const selfPhotos = galleryOverride ?? (user?.profile_photos || []);
  const showSelfGallery = isSelf && selfLimit > 1;
  const otherPhotos = u.profile_photos || [];
  const avatarUrl = isSelf ? (selfPhotos[0]?.url || u.avatar) : (otherPhotos[0]?.url || u.avatar);
  const galleryStrip = isSelf ? selfPhotos : otherPhotos; // position 0 is the avatar

  // Suspended (disabled/archived) accounts render a neutral "unavailable" page, never a 404.
  if (data.unavailable || u.unavailable) return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="text-center py-24 px-4" data-testid="profile-unavailable">
        <h1 className="font-heading text-3xl uppercase tracking-tight">This account is unavailable</h1>
        <p className="text-sm text-muted-foreground font-body mt-2">@{u.username} isn't active on OpticSwap right now.</p>
        <p className="text-sm text-muted-foreground font-body mt-1" data-testid="profile-unavailable-contact">Email <a href="mailto:sales@opticswap.com" className="text-olive-bright hover:underline" data-testid="profile-unavailable-email">sales@opticswap.com</a> if you have questions.</p>
        <button onClick={() => navigate("/feed")} className="mt-5 inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-4 py-2 transition-colors">Back to marketplace</button>
      </div>
    </div>
  );

  const submitReview = async () => {
    try {
      await api.post(`/users/${username}/reviews`, { rating, text: reviewText });
      toast.success("Feedback submitted.");
      setReviewText("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submitReport = async () => {
    if (!reportCat) { toast.error("Please choose a report category."); return; }
    setReportBusy(true);
    try {
      await api.post(`/users/${username}/report`, { category: reportCat, reason: reportCats.find((c) => c.id === reportCat)?.label || "", comment: reportComment });
      toast.success("Report submitted to moderators.");
      setReportOpen(false);
      setReportComment("");
      setReportCat("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setReportBusy(false); }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        {isSelf && <MemberWarningsBanner className="mb-4" />}
        {previewPublic && (
          <div data-testid="profile-preview-banner" className="mb-4 flex items-center justify-between gap-3 border border-olive/40 bg-olive/5 px-4 py-2.5">
            <span className="font-mono text-[11px] uppercase tracking-wide text-olive-bright flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> This is what other members see
            </span>
            <button onClick={() => navigate("/profile")} data-testid="profile-preview-exit"
              className="inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-1.5 transition-colors">
              <X className="w-3.5 h-3.5" /> Exit preview
            </button>
          </div>
        )}
        {movedFrom && (
          <div data-testid="profile-moved-note" className="mb-4 border border-volt/40 bg-volt/5 text-volt font-mono text-[11px] uppercase tracking-wide px-3 py-2 flex items-center gap-2">
            <Repeat className="w-3.5 h-3.5" /> @{movedFrom} is now <span className="text-foreground">@{u.username}</span>
          </div>
        )}
        {/* header bento */}
        <div className="border border-sniper bg-surface overflow-hidden" data-testid="profile-header">
          <VerificationEdge identityVerified={u.identity_verified} dealerVerified={u.dealer_verified} height={4} />
          <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="w-24 h-24 border border-sniper bg-obsidian flex items-center justify-center overflow-hidden shrink-0 relative group">
                {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" /> : <span className="font-heading text-4xl" data-testid="profile-avatar-monogram">{u.username[0]?.toUpperCase()}</span>}
                {isSelf && !showSelfGallery && (
                  <label data-testid="profile-avatar-upload" title="Change profile photo"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-obsidian/75 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <Camera className="w-5 h-5 text-foreground" />
                    <span className="font-mono text-[8px] uppercase text-foreground tracking-wide">{uploading ? "…" : "Photo"}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={onAvatarFile} disabled={uploading} data-testid="profile-avatar-input" />
                  </label>
                )}
              </div>

              {/* limit-1 accounts (almost everyone): plain Replace + Remove, NO gallery chrome */}
              {isSelf && !showSelfGallery && (
                <div className="flex items-center gap-1.5">
                  <label data-testid="profile-avatar-replace" title="Upload a new photo"
                    className={`inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2 py-1 transition-colors cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                    <Camera className="w-3 h-3" /> {(u.account_type === "business" || u.is_dealer) ? (avatarUrl ? "Replace logo" : "Add logo") : (avatarUrl ? "Replace" : "Add photo")}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={onAvatarFile} disabled={uploading} data-testid="profile-avatar-replace-input" />
                  </label>
                  {avatarUrl && (
                    <button onClick={openRecrop} disabled={uploading} data-testid="profile-avatar-recrop" title="Adjust the crop"
                      className="inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2 py-1 transition-colors disabled:opacity-50">
                      <Crop className="w-3 h-3" /> Re-crop
                    </button>
                  )}
                  {avatarUrl && (
                    <button onClick={onAvatarRemove} disabled={uploading} data-testid="profile-avatar-remove" title="Remove photo"
                      className="inline-flex items-center gap-1 border border-sniper hover:border-blaze text-secondary-foreground hover:text-blaze font-mono uppercase text-[10px] px-2 py-1 transition-colors disabled:opacity-50">
                      <X className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
              )}

              {/* limit>1 accounts (admins, ambassadors, allowlist): gallery */}
              {showSelfGallery && (
                <div className="flex flex-col items-center gap-2" data-testid="profile-gallery">
                  <span className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="profile-gallery-count">{selfPhotos.length} of {selfLimit}</span>
                  <div className="flex flex-wrap gap-1.5 max-w-[224px] justify-center">
                    {selfPhotos.map((p, i) => (
                      <div key={p.photo_id} className="relative w-12 h-12 border border-sniper overflow-hidden group/th" data-testid={`profile-gallery-thumb-${i}`}>
                        <img src={p.thumb_url || p.url} alt="" onClick={() => setLightbox(p.url)} className="w-full h-full object-cover cursor-pointer" />
                        {i === 0 && <span className="absolute top-0 left-0 bg-olive text-white px-1 text-[8px] font-mono uppercase" title="Profile photo">Main</span>}
                        <div className="absolute inset-0 flex items-center justify-center gap-1 bg-obsidian/80 opacity-0 group-hover/th:opacity-100 transition-opacity">
                          {i !== 0 && (
                            <button onClick={() => setPrimaryPhoto(p.photo_id)} title="Set as profile photo" data-testid={`profile-gallery-primary-${i}`} className="text-foreground hover:text-olive-bright"><Star className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => removePhoto(p.photo_id)} title="Remove photo" data-testid={`profile-gallery-remove-${i}`} className="text-foreground hover:text-blaze"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                    {selfPhotos.length < selfLimit ? (
                      <label data-testid="profile-gallery-add" title="Add a photo"
                        className={`w-12 h-12 border border-dashed border-sniper hover:border-olive flex items-center justify-center cursor-pointer text-secondary-foreground hover:text-foreground transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                        <Plus className="w-4 h-4" />
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={onAvatarFile} disabled={uploading} />
                      </label>
                    ) : (
                      <div data-testid="profile-gallery-full" title={`Photo limit reached (${selfLimit}). Remove one to add another.`}
                        className="w-12 h-12 border border-dashed border-sniper/50 flex items-center justify-center text-muted-foreground/50 cursor-not-allowed"><Plus className="w-4 h-4" /></div>
                    )}
                  </div>
                  {selfPhotos.length >= selfLimit && (
                    <span className="font-mono text-[9px] uppercase text-muted-foreground text-center max-w-[220px]" data-testid="profile-gallery-full-note">Photo limit reached ({selfLimit}). Remove one to add another.</span>
                  )}
                </div>
              )}

              {/* others viewing a multi-photo account: read-only strip + lightbox */}
              {!isSelf && otherPhotos.length > 1 && (
                <div className="flex flex-wrap gap-1.5 max-w-[224px] justify-center" data-testid="profile-gallery-readonly">
                  {otherPhotos.map((p, i) => (
                    <img key={i} src={p.thumb_url || p.url} alt="" onClick={() => setLightbox(p.url)} className="w-12 h-12 border border-sniper object-cover cursor-pointer" />
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-y-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="font-heading text-4xl font-bold tracking-tight" data-testid="profile-username">@{u.username}</h1>
                  <FoundingSeatChip seat={u.founding_number} accountType={u.account_type} testid="profile-founding" />
                </div>
                {u.vouch?.invited_by_username && (
                  <VouchMark inviter={u.vouch.invited_by_username} size={18} testid="profile-vouch-mark" />
                )}
              </div>
              <BadgeRow badges={u.earned_badges} accountTone={u.account_tone} className="mt-2.5" />
              {isSelf && u.founding_number && <FoundingShareCard seat={u.founding_number} className="mt-3 max-w-sm" />}
              {isSelf && <FoundingClaimPrompt mode="callout" className="mt-3" />}
              {!u.vouch?.invited_by_username && isSelf && data.vouch_self?.state === "pending_verification" && (
                <p className="mt-3 font-mono text-[11px] tracking-wide" data-testid="profile-vouch-pending" style={{ color: "#8E9195" }}>
                  Your invite from <span style={{ color: "#8FA84A" }}>@{data.vouch_self.inviter_username}</span> shows on your profile once you're ID-verified.
                </p>
              )}
              {u.bio && <p className="text-sm text-secondary-foreground font-body mt-2">{u.bio}</p>}
              {u.specialty_tags && u.specialty_tags.length > 0 && (
                <div className="mt-3 pt-3 border-t border-sniper/60" data-testid="profile-specialty-divider">
                  <SpecialtyRow tags={u.specialty_tags} />
                </div>
              )}
            </div>
            {user && !isOwner && (
              <div className="flex items-center gap-2 shrink-0">
                <BlockUserButton username={u.username} blocked={blocked} onChanged={setBlocked} />
                <button
                  onClick={() => setReportOpen(true)}
                  data-testid="profile-report-btn"
                  className="inline-flex items-center gap-1.5 border border-sniper hover:border-blaze text-secondary-foreground hover:text-blaze font-mono uppercase text-[11px] px-3 py-2 transition-colors"
                  title="Report this user"
                >
                  <Flag className="w-3.5 h-3.5" /> Report
                </button>
              </div>
            )}
            {isSelf && (
              <div className="flex flex-col items-stretch gap-2 shrink-0">
                <button
                  onClick={() => navigate("/settings/profile")}
                  data-testid="profile-edit-btn"
                  className="inline-flex items-center justify-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-2 transition-colors"
                  title="Edit your profile & settings"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit Profile
                </button>
                <button
                  onClick={() => navigate(`/u/${u.username}?view=public`)}
                  data-testid="profile-preview-public-btn"
                  className="inline-flex items-center justify-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-2 transition-colors"
                  title="See your profile exactly as other members see it"
                >
                  <Eye className="w-3.5 h-3.5" /> Preview public profile
                </button>
              </div>
            )}
          </div>
          <Crosshair className="my-5" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono">
            <div><div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Star className="w-3 h-3 text-volt" /> Feedback</div>{u.feedback_count >= 1 ? (<div className="font-heading text-2xl">{u.feedback_score} <span className="text-sm text-muted-foreground">({u.feedback_count})</span></div>) : (<div className="font-mono text-xs mt-1.5" style={{ color: "#8E9195" }}>No reviews yet</div>)}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Active Listings</div><div className="font-heading text-2xl">{data.active_listings}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> Joined</div><div className="font-heading text-lg">{new Date(u.created_at).toLocaleDateString()}</div></div>
            {u.feedback_count >= 1 && Number.isFinite(u.response_rate) && (
              <div><div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Response</div><div className="font-heading text-2xl">{u.response_rate}%</div></div>
            )}
          </div>
          </div>
        </div>

        {/* listings */}
        <h2 className="font-heading text-2xl uppercase tracking-tight mt-8 mb-3">Listings</h2>
        <Crosshair className="my-3" />
        {listings.length ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {listings.map((l, i) => <ListingCard key={l.listing_id} listing={l} index={i} />)}
          </div>
        ) : <p className="text-sm text-muted-foreground font-body">No active listings.</p>}

        {/* reviews */}
        <h2 className="font-heading text-2xl uppercase tracking-tight mt-10 mb-3">Feedback ({data.reviews.length})</h2>
        <Crosshair className="my-3" />
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-3">
            {data.reviews.map((r) => (
              <div key={r.review_id} className="border border-sniper bg-surface p-4" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-olive-bright">@{r.reviewer_username}</span>
                  <span className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "text-volt fill-volt" : "text-sniper"}`} />)}</span>
                </div>
                {r.text && <p className="text-sm text-secondary-foreground font-body mt-1">{r.text}</p>}
              </div>
            ))}
            {data.reviews.length === 0 && <p className="text-sm text-muted-foreground font-body">No feedback yet.</p>}
          </div>

          {user && !isOwner && (
            <div className="border border-sniper bg-surface p-4 h-fit" data-testid="leave-review">
              <h3 className="font-heading text-lg uppercase">Leave Feedback</h3>
              <div className="flex gap-1 my-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <button key={i} onClick={() => setRating(i + 1)} data-testid={`star-${i + 1}`}>
                    <Star className={`w-6 h-6 ${i < rating ? "text-volt fill-volt" : "text-sniper"} transition-colors`} />
                  </button>
                ))}
              </div>
              <textarea rows={3} value={reviewText} onChange={(e) => setReviewText(e.target.value)} data-testid="review-text"
                placeholder="How was the transaction?" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground transition-colors" />
              <button onClick={submitReview} data-testid="review-submit" className="w-full mt-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase py-2.5 transition-colors">Submit</button>
            </div>
          )}
        </div>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="profile-report-modal" onClick={() => !reportBusy && setReportOpen(false)}>
          <div className="w-full max-w-md border border-sniper bg-surface p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl uppercase tracking-tight text-foreground flex items-center gap-2">
                <Flag className="w-4 h-4 text-blaze" /> Report @{u.username}
              </h3>
              <button onClick={() => setReportOpen(false)} data-testid="profile-report-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs font-body text-secondary-foreground mt-2">Reports go to the moderation team and appear in SITREP. Abuse of reporting may be actioned.</p>
            <label className="block font-mono text-[10px] uppercase text-muted-foreground mt-4 mb-1">Reason</label>
            <select
              value={reportCat}
              onChange={(e) => setReportCat(e.target.value)}
              data-testid="profile-report-reason"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm transition-colors"
            >
              <option value="" disabled>Choose a category…</option>
              {reportCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            {reportCat && <p className="text-[11px] font-mono text-muted-foreground mt-1.5 leading-snug">{reportCats.find((c) => c.id === reportCat)?.definition}</p>}
            <label className="block font-mono text-[10px] uppercase text-muted-foreground mt-3 mb-1">Details (optional)</label>
            <textarea
              rows={3}
              value={reportComment}
              onChange={(e) => setReportComment(e.target.value)}
              data-testid="profile-report-comment"
              placeholder="What happened?"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm transition-colors"
            />
            <button
              onClick={submitReport}
              disabled={reportBusy}
              data-testid="profile-report-submit"
              className="w-full mt-4 bg-blaze hover:bg-blaze/90 disabled:opacity-60 text-white font-heading font-bold uppercase py-2.5 transition-colors"
            >
              {reportBusy ? "Submitting…" : "Submit Report"}
            </button>
          </div>
        </div>
      )}

      {cropper && (
        <ImageCropper
          source={cropper.source}
          initialTransform={cropper.initialTransform}
          label={(data?.user?.account_type === "business" || data?.user?.is_dealer) ? "Position your logo" : "Position your photo"}
          busy={cropBusy}
          onSave={saveCrop}
          onCancel={() => setCropper(null)}
        />
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/85 backdrop-blur-sm" data-testid="profile-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} data-testid="profile-lightbox-close" className="absolute top-4 right-4 text-secondary-foreground hover:text-foreground"><X className="w-6 h-6" /></button>
          <img src={lightbox} alt="" className="max-w-[90vw] max-h-[85vh] object-contain border border-sniper" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
